import { css } from '@emotion/core';
import {
    Avatar,
    IconButton,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Theme,
    Tooltip,
    Typography,
    useTheme
} from '@material-ui/core';
import { Assignment, Business, CheckCircleOutline } from '@material-ui/icons';
import React from 'react';

import { notificationTime } from '../../common/timestamp';
import { ClientUpdateDraft, JobUpdateDraft } from '../../graphql/queries/notifications';
import { useSession } from '../../hooks/use-session';
import { UpdateDiffDialog } from '../agent/update-diff-dialog';
import { notificationStyles } from './notification-styles';

const styles = (theme: Theme) => css`
    ${notificationStyles(theme)}

    .explanation {
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .item-actions {
        display: flex;
        position: absolute;
        bottom: 10px;
        right: 12px;
        opacity: 0;

        .MuiIconButton-root {
            margin-left: 5px;
        }
    }

    &:hover {
        background: rgba(0, 0, 0, 0.025);
        .item-actions {
            opacity: 1;
        }
    }
`;

type UpdateDraftNotificationProps =
    | {
          draftType: 'job';
          draft: JobUpdateDraft;
          userId: string;
          onComplete: () => void;
      }
    | {
          draftType: 'client';
          draft: ClientUpdateDraft;
          userId: string;
          onComplete: () => void;
      };

export const UpdateDraftNotification: React.FC<UpdateDraftNotificationProps> = (props) => {
    const { draftType, draft, userId, onComplete } = props;
    const theme = useTheme();
    const { user } = useSession();
    const [dialogOpen, setDialogOpen] = React.useState(false);

    const handleClick = () => {
        setDialogOpen(true);
    };

    const handleClose = () => {
        setDialogOpen(false);
    };

    const actions =
        user.id === userId ? (
            <div className="item-actions">
                <Tooltip title="OK">
                    <IconButton size="small" onClick={onComplete}>
                        <CheckCircleOutline fontSize="small" />
                    </IconButton>
                </Tooltip>
            </div>
        ) : null;

    const isJob = draftType === 'job';
    const title = isJob
        ? `${(draft as JobUpdateDraft).job.client.name} - ${(draft as JobUpdateDraft).job.title}`
        : (draft as ClientUpdateDraft).client.name;
    const label = isJob ? 'Job update draft' : 'Client update draft';
    const Icon = isJob ? Assignment : Business;

    const primary = <Typography variant="h6">{title}</Typography>;
    const secondary = (
        <Typography variant="body2" color="textSecondary" component="div">
            {label}
            {draft.explanation && <div className="explanation">{draft.explanation}</div>}
        </Typography>
    );

    return (
        <>
            <ListItem css={styles(theme)} onClick={handleClick} button={true}>
                <ListItemAvatar>
                    <Avatar>
                        <Icon />
                    </Avatar>
                </ListItemAvatar>
                <ListItemText disableTypography={true} primary={primary} secondary={secondary} />
                <div>
                    <Typography color="textSecondary" noWrap={true} variant="caption" component="div">
                        <span className="left-pad">{notificationTime(draft.createdAt)}</span>
                    </Typography>
                </div>
                {actions}
            </ListItem>
            <UpdateDiffDialog open={dialogOpen} onClose={handleClose} draftId={draft.id} draftType={draftType} />
        </>
    );
};
